/**
 * MetricHelp — what a figure means, the numbers behind it and how it is
 * worked out (dashboard tiles, column headers, a row's figures and flags).
 * Web: a tooltip; the app's MetricHelp (apps/mobile/src/components/common/ds)
 * is a bottom sheet with the same `explanation`. Content shape and layout:
 * packages/ui theme/metrics.ts (`MetricExplanation`, `metricHelpLayout`,
 * `metricHelpSpec`).
 *
 * Card: heading `cardTitle`; intro lines `body`; each entry's name in `label`,
 * its meaning `body`, its numbers `value` (tabular), its working `meta`;
 * notes `meta`. Compact: one `label` + numbers line per entry.
 *
 * The tooltip opens on hover, on keyboard focus of its info button, and on tap
 * (touch has no hover); Escape or a tap elsewhere closes it. It renders in a
 * portal with fixed positioning (tables clip their overflow) and flips above
 * its trigger when there is no room below.
 *
 * With `icon` (the default) an info button follows the children and is the
 * keyboard/touch target. Without it (table cells, chips) the children
 * themselves are the target: hover on a desktop, tap on a touch screen.
 * `iconFirst` puts the button before the children (a right-aligned column
 * header, whose label must end where the figures below it end). `hoverIcon`
 * opens it on hover of the info button alone, for children that are controls
 * of their own (a sort heading), so pointing at them to click does not cover
 * the rows.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { metricHelpLayout, metricHelpSpec, textRole, tokenVars, type MetricExplanation } from '@turn-connect/ui';

const S = metricHelpSpec;

export interface MetricHelpCardProps {
  explanation: MetricExplanation;
  testId?: string;
}

/** The explanation itself: the tooltip's content (and anywhere it is shown in place). */
export const MetricHelpCard: React.FC<MetricHelpCardProps> = ({ explanation: e, testId }) => {
  const { heading, entryTitles } = metricHelpLayout(e);
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: S.entryGap, minWidth: 0 }}>
      {(heading || (e.intro && e.intro.length > 0)) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: S.gap }}>
          {heading && <div style={textRole('cardTitle')}>{heading}</div>}
          {e.intro?.map((line) => (
            <div key={line} style={textRole('body')}>
              {line}
            </div>
          ))}
        </div>
      )}
      {e.helps.map((h) => (
        <div
          key={h.key}
          data-testid={testId ? `${testId}-${h.key}` : undefined}
          style={{ display: 'flex', flexDirection: 'column', gap: S.gap }}
        >
          {e.compact ? (
            <div style={textRole('body')}>
              {entryTitles && <span style={{ ...textRole('label'), marginRight: 6 }}>{h.title}</span>}
              <span style={{ ...textRole('value'), fontVariantNumeric: 'tabular-nums' }}>{h.figures.join(' · ')}</span>
            </div>
          ) : (
            <>
              {entryTitles && <div style={textRole('label')}>{h.title}</div>}
              <div style={textRole('body')}>{h.meaning}</div>
              {h.figures.length > 0 && (
                <ul style={{ ...textRole('value'), margin: 0, paddingLeft: 18, fontVariantNumeric: 'tabular-nums' }}>
                  {h.figures.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              )}
              <div style={textRole('meta')}>{h.calculation}</div>
            </>
          )}
        </div>
      ))}
      {e.notes && e.notes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: S.gap }}>
          {e.notes.map((line) => (
            <div key={line} style={textRole('meta')}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export interface MetricHelpProps {
  explanation: MetricExplanation;
  /** Accessible name of the info button: "About Hours turned". */
  label: string;
  icon?: boolean;
  iconFirst?: boolean;
  hoverIcon?: boolean;
  /**
   * `press` (no info icon): opens on a click, tap or Enter on the children only,
   * never on hover — for a whole tile, where hovering past it should not open a tip.
   */
  openOn?: 'hover' | 'press';
  /** On the open tooltip; the card's entries are `<testId>-<key>`. */
  testId?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export const MetricHelp: React.FC<MetricHelpProps> = ({
  explanation,
  label,
  icon = true,
  iconFirst = false,
  hoverIcon = false,
  openOn = 'hover',
  testId,
  children,
  style,
}) => {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const clearTimer = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  // When the tip opened: a scroll still settling from the tap that opened it
  // (the browser bringing the button into view) must not close it again.
  const openedAt = useRef(0);
  const show = useCallback(() => {
    clearTimer();
    openedAt.current = Date.now();
    setOpen(true);
  }, []);
  const hideSoon = useCallback(() => {
    clearTimer();
    if (pinned) return;
    // A moment's grace so the pointer can travel from the trigger into the tip.
    hoverTimer.current = setTimeout(() => setOpen(false), 120);
  }, [pinned]);
  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPinned(false);
  }, []);

  const place = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(S.tooltipWidth, vw - S.tooltipGap * 2);
    const left = Math.max(S.tooltipGap, Math.min(r.left, vw - width - S.tooltipGap));
    const height = tipRef.current?.offsetHeight ?? 0;
    const above = r.bottom + S.tooltipGap + height > vh && r.top - S.tooltipGap - height > 0;
    setPos({ left, top: above ? r.top - S.tooltipGap - height : r.bottom + S.tooltipGap });
  }, []);
  useLayoutEffect(() => {
    if (open) place();
  }, [open, explanation, place]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || tipRef.current?.contains(t)) return;
      close();
    };
    // The page scrolls under a fixed tip: close rather than float away —
    // except for the scroll that brought the trigger into view as it opened.
    const onScroll = () => {
      if (Date.now() - openedAt.current < 600) place();
      else close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close, place]);

  useEffect(() => clearTimer, []);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open && pinned) close();
    else {
      setPinned(true);
      show();
    }
  };

  // A pressed child (a whole tile) is the control: it says the tip is open,
  // and the tip describes it, so a screen reader hears both.
  const pressChild = !icon && openOn === 'press' && React.isValidElement(children);
  const trigger = pressChild
    ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        'aria-expanded': open,
        'aria-describedby': open ? id : undefined,
      })
    : children;

  return (
    <span
      ref={anchorRef}
      // A row that opens on click leaves clicks on a tip to the tip.
      data-metric-help=""
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%', ...style }}
      onMouseEnter={hoverIcon || openOn === 'press' ? undefined : show}
      onMouseLeave={hoverIcon || openOn === 'press' ? undefined : hideSoon}
      onClick={icon ? undefined : toggle}
      aria-describedby={!icon && !pressChild && open ? id : undefined}
    >
      {!iconFirst && trigger}
      {icon && (
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-describedby={open ? id : undefined}
          onClick={toggle}
          onMouseEnter={hoverIcon ? show : undefined}
          onMouseLeave={hoverIcon ? hideSoon : undefined}
          onFocus={show}
          onBlur={hideSoon}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            // Small to the eye, but a comfortable target: padding widens the hit area.
            padding: 4,
            margin: -4,
            background: 'none',
            border: 0,
            borderRadius: 999,
            color: open ? tokenVars.text.primary : tokenVars.text.muted,
            cursor: 'help',
          }}
        >
          <Info size={13} aria-hidden />
        </button>
      )}
      {iconFirst && trigger}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            data-testid={testId}
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            // React bubbles portal events through the component tree: keep a
            // click inside the tip from reaching a row or a cell's toggle.
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              zIndex: 10000,
              width: Math.min(S.tooltipWidth, window.innerWidth - S.tooltipGap * 2),
              boxSizing: 'border-box',
              // A row's figures can outgrow a short window: scroll inside the tip.
              maxHeight: `calc(100vh - ${S.tooltipGap * 2}px)`,
              overflowY: 'auto',
              padding: '12px 14px',
              background: tokenVars.surfaceRaised,
              border: `1px solid ${tokenVars.border}`,
              borderRadius: S.radius,
              boxShadow: `0 12px 32px ${tokenVars.overlay}`,
              textAlign: 'left',
              whiteSpace: 'normal',
              letterSpacing: 'normal',
              textTransform: 'none',
              cursor: 'auto',
            }}
          >
            <MetricHelpCard explanation={explanation} testId={testId ? `${testId}-card` : undefined} />
          </div>,
          document.body
        )}
    </span>
  );
};

export default MetricHelp;
